/**
 * Cart quantity (+ / -) and delete (bin) controls.
 *
 * A self-contained, dependency-free handler so the cart buttons keep working even
 * if another cart script fails to load. It listens on the document in the capture
 * phase, handles buttons inside <s-cart> with command="--update-quantity" or
 * command="--remove", and stops the event so it is not handled twice.
 *
 * Button markup (rendered by snippets/cart-line-item.liquid and the cart templates):
 *   data-line-key     — cart line key
 *   data-delta        — +/- step for quantity buttons
 *   data-qty-min      — minimum quantity (default 1)
 *   data-qty-max      — maximum quantity (available stock); never exceeded
 *   data-max-message  — message shown when the shopper tries to go past the stock limit
 */
(() => {
  if (window.__cartControlsInstalled) return;
  window.__cartControlsInstalled = true;

  const timers = new Map();
  const pending = new Map();
  let queue = Promise.resolve();

  const announcer = () => document.getElementById('sr-announcer');
  const message = (key) => announcer()?.dataset[key] || '';

  function toast(text, variant = 'success') {
    if (!text) return;
    document.dispatchEvent(new CustomEvent('s-notification:add', {
      detail: { message: text, variant, duration: 3000 },
    }));
    const live = announcer();
    if (live) live.textContent = text;
  }

  function changeUrl(cart) {
    const base = cart?.dataset.cartChangeUrl || (window.Shopify?.routes?.root || '/') + 'cart/change';
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  function lineIndex(cart, lineKey) {
    const keys = [...cart.querySelectorAll('[data-quantity-display][data-line-key]')]
      .map((el) => el.dataset.lineKey);
    const index = keys.indexOf(lineKey);
    return index >= 0 ? index + 1 : null;
  }

  async function refresh() {
    const CartElement = customElements.get('s-cart');
    try {
      if (CartElement && typeof CartElement.refreshMountedCarts === 'function') {
        await CartElement.refreshMountedCarts();
        return;
      }
    } catch (error) {
      console.error('Cart refresh failed; reloading.', error);
    }
    window.location.reload();
  }

  async function postChange(cart, lineKey, quantity) {
    const url = changeUrl(cart);
    const send = (body) => fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    let response = await send({ id: lineKey, quantity });
    if (!response.ok) {
      const line = lineIndex(cart, lineKey);
      if (line) response = await send({ line, quantity });
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || message('updateError'));
    return data;
  }

  // Run cart changes one at a time so quick clicks never race each other.
  function commit(cart, lineKey, quantity, row) {
    queue = queue.then(async () => {
      try {
        await postChange(cart, lineKey, quantity);
        if (quantity === 0) toast(message('removedMessage'));
      } catch (error) {
        if (row) row.style.removeProperty('display');
        toast(error.message || message('updateError'), 'error');
      } finally {
        pending.delete(lineKey);
        if (!timers.size && !pending.size) await refresh();
      }
    });
    return queue;
  }

  function findRow(button) {
    return button.closest('[style*="view-transition-name"]');
  }

  function handleRemove(cart, button) {
    const lineKey = button.dataset.lineKey;
    if (!lineKey) return;
    clearTimeout(timers.get(lineKey));
    timers.delete(lineKey);
    const row = findRow(button);
    if (row) row.style.display = 'none';
    pending.set(lineKey, 0);
    commit(cart, lineKey, 0, row);
  }

  function handleQuantity(cart, button) {
    const lineKey = button.dataset.lineKey;
    if (!lineKey) return;
    const display = cart.querySelector(`[data-quantity-display][data-line-key="${CSS.escape(lineKey)}"]`);
    if (!display) return;

    const current = parseInt(display.value ?? display.textContent, 10) || 0;
    const delta = parseInt(button.dataset.delta, 10) || 0;
    const max = button.dataset.qtyMax !== undefined && button.dataset.qtyMax !== ''
      ? parseInt(button.dataset.qtyMax, 10)
      : Infinity;
    let next = current + delta;

    if (delta > 0 && next > max) {
      toast(button.dataset.maxMessage || message('updateError'), 'error');
      return;
    }
    if (next < 0) next = 0;

    if (next === 0) {
      const removeButton = cart.querySelector(`button[command="--remove"][data-line-key="${CSS.escape(lineKey)}"]`);
      handleRemove(cart, removeButton || button);
      return;
    }

    if ('value' in display) display.value = next; else display.textContent = next;

    // Grey out + once the stock limit is reached.
    const plus = cart.querySelector(`button[command="--update-quantity"][data-line-key="${CSS.escape(lineKey)}"][data-delta]:not([data-delta^="-"])`);
    if (plus) {
      if (next >= max) plus.setAttribute('aria-disabled', 'true');
      else plus.removeAttribute('aria-disabled');
    }

    clearTimeout(timers.get(lineKey));
    timers.set(lineKey, setTimeout(() => {
      timers.delete(lineKey);
      pending.set(lineKey, next);
      commit(cart, lineKey, next, null);
    }, 300));
  }

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('button[command="--update-quantity"], button[command="--remove"]')
      : null;
    if (!button) return;
    const cart = button.closest('s-cart');
    if (!cart) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (button.getAttribute('command') === '--remove') handleRemove(cart, button);
    else handleQuantity(cart, button);
  }, true);
})();
